import React, { useEffect, useState, useCallback } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import axios from '../../../../../config/axios'

import lookup from '../../../../../assets/equipment/Tables/lookup.svg'
import trash from '../../../../../assets/equipment/Tables/trash.svg'
import pencil from '../../../../../assets/equipment/Tables/pencil.svg'

import { EquipmentOutletType } from '../../index'

import ViewVehicleModal from '../ViewVehicleModal/index'
import EditVehicleModal from '../EditVehicleModal/index'

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/equipment`

interface BackendVehicleListItem {
  id: string
  plate: string
  model: string
  year: number
  capacity: string
  bodyType: string
  restriction: string | null
  periodicMaintenances: Array<{
    id?: number
    maintenanceName: string
    periodicityValue: number
    periodicityUnit: string
  }>
}

type ExistingVehicleData = BackendVehicleListItem

const VehicleTable: React.FC = () => {
  const { refresh, search, Styled } = useOutletContext<EquipmentOutletType>()
  const {
    GlobalTable,
    HeaderLine,
    Th,
    Tr,
    Td,
    TdButtons,
    DivInTd,
    InfoMessage,
  } = Styled
  const navigate = useNavigate()

  const [vehicles, setVehicles] = useState<BackendVehicleListItem[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const [showViewModal, setShowViewModal] = useState<boolean>(false)
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(
    null,
  )

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [vehicleToEdit, setVehicleToEdit] =
    useState<ExistingVehicleData | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    const token = localStorage.getItem('token')

    if (!token) {
      setError('Sessão expirada. Por favor, faça login novamente.')
      setLoading(false)
      navigate('/login')
      return
    }

    try {
      const response = await axios.get<any[]>(`${API_BASE_URL}/vehicles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      let fetchedVehicles: BackendVehicleListItem[] = response.data.map(
        (item) => ({
          id: String(item.id),
          plate: item.plate,
          model: item.model,
          year: item.year,
          capacity: item.capacity || '',
          bodyType: item.bodyType || '',
          restriction: item.restriction === undefined ? null : item.restriction,
          periodicMaintenances: item.periodicMaintenances || [],
        }),
      )

      if (search) {
        const searchTermLower = search.toLowerCase()
        fetchedVehicles = fetchedVehicles.filter(
          (item) =>
            item.plate.toLowerCase().includes(searchTermLower) ||
            item.model.toLowerCase().includes(searchTermLower) ||
            item.year.toString().includes(searchTermLower) ||
            item.restriction?.toLowerCase().includes(searchTermLower) ||
            item.bodyType?.toLowerCase().includes(searchTermLower) ||
            item.capacity.toLowerCase().includes(searchTermLower),
        )
      }
      setVehicles(fetchedVehicles)
    } catch (err) {
      console.error('Erro ao buscar dados dos veículos:', err)
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('Não autorizado. Por favor, faça login novamente.')
        navigate('/login')
      } else {
        setError('Não foi possível carregar os veículos. Tente novamente.')
      }
    } finally {
      setLoading(false)
    }
  }, [search, refresh, navigate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleViewDetails = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId)
    setShowViewModal(true)
  }

  const handleCloseViewModal = () => {
    setShowViewModal(false)
    setSelectedVehicleId(null)
  }

  const handleEdit = (vehicle: BackendVehicleListItem) => {
    setVehicleToEdit(vehicle)
    setIsEditModalOpen(true)
  }

  const handleSaveEditVehicleModal = async (
    vehicleId: string,
    updatedData: any,
  ): Promise<boolean> => {
    const token = localStorage.getItem('token')
    if (!token) {
      alert('Sessão expirada. Por favor, faça login novamente.')
      navigate('/login')
      return false
    }

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/vehicles/${vehicleId}`,
        updatedData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.status === 200) {
        alert('Veículo atualizado com sucesso!')
        setIsEditModalOpen(false)
        setVehicleToEdit(null)
        fetchData()
        return true
      } else {
        alert(
          `Erro ao atualizar veículo: ${
            response.statusText || 'Resposta inesperada.'
          }`,
        )
        return false
      }
    } catch (err) {
      console.error('Erro ao atualizar veículo:', err)
      let errorMessage = 'Ocorreu um erro ao atualizar o veículo.'
      if (axios.isAxiosError(err)) {
        errorMessage = err.response?.data?.message || err.message
        if (err.response?.status === 401) {
          errorMessage = 'Não autorizado. Por favor, faça login novamente.'
          navigate('/login')
        } else if (err.response?.status === 404) {
          errorMessage = 'Veículo não encontrado.'
        } else if (err.response?.status === 400) {
          errorMessage = `Dados inválidos: ${
            err.response?.data?.details ||
            err.response?.data?.message ||
            'Verifique os campos preenchidos.'
          }`
        }
      }
      alert(`Falha na atualização: ${errorMessage}`)
      return false
    }
  }

  const handleDelete = async (vehicleId: string) => {
    if (
      window.confirm(
        `Tem certeza que deseja excluir o veículo com ID: ${vehicleId}?`,
      )
    ) {
      const token = localStorage.getItem('token')
      if (!token) {
        alert('Sessão expirada. Por favor, faça login novamente.')
        navigate('/login')
        return
      }

      try {
        setLoading(true)
        setError(null)
        await axios.delete(`${API_BASE_URL}/vehicles/${vehicleId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        setVehicles((prevVehicles) =>
          prevVehicles.filter((vehicle) => vehicle.id !== vehicleId),
        )
        setLoading(false)
        alert('Veículo excluído com sucesso!')
      } catch (err) {
        console.error(`Erro ao excluir veículo ${vehicleId}:`, err)
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          setError(
            'Não autorizado para excluir. Por favor, faça login novamente.',
          )
          navigate('/login')
        } else {
          setError(`Falha ao excluir o veículo ${vehicleId}.`)
        }
        setLoading(false)
      }
    }
  }

  if (loading) {
    return <InfoMessage>Carregando veículos...</InfoMessage>
  }

  if (error) {
    return <InfoMessage style={{ color: 'red' }}>{error}</InfoMessage>
  }

  if (vehicles.length === 0) {
    return <InfoMessage>Nenhum veículo encontrado.</InfoMessage>
  }

  const TableContent: React.FC = () => {
    return (
      <>
        <GlobalTable>
          <thead>
            <HeaderLine>
              <Th>Placa</Th>
              <Th>Modelo</Th>
              <Th>Ano</Th>
              <Th>Capacidade</Th>
              <Th>Restrição</Th>
              <Th></Th>
            </HeaderLine>
          </thead>
          <tbody>
            {vehicles.map((vehicle) => (
              <Tr key={vehicle.id}>
                <Td data-label='Placa'>{vehicle.plate}</Td>
                <Td data-label='Modelo'>{vehicle.model}</Td>
                <Td data-label='Ano'>{vehicle.year}</Td>
                <Td data-label='Capacidade de Carga'>{vehicle.capacity}</Td>
                <Td data-label='Restrição de Rodagem'>
                  {vehicle.restriction || 'N/A'}
                </Td>
                <TdButtons>
                  <DivInTd onClick={() => handleViewDetails(vehicle.id)}>
                    <img
                      src={lookup}
                      alt='Ver detalhes'
                      title='Ver detalhes'
                      style={{ cursor: 'pointer' }}
                    />
                  </DivInTd>
                  <DivInTd onClick={() => handleEdit(vehicle)}>
                    <img
                      src={pencil}
                      alt='Editar'
                      title='Editar'
                      style={{ cursor: 'pointer' }}
                    />
                  </DivInTd>
                  <DivInTd onClick={() => handleDelete(vehicle.id)}>
                    <img
                      src={trash}
                      alt='Excluir'
                      title='Excluir'
                      style={{ cursor: 'pointer' }}
                    />
                  </DivInTd>
                </TdButtons>
              </Tr>
            ))}
          </tbody>
        </GlobalTable>

        {showViewModal && selectedVehicleId !== null && (
          <ViewVehicleModal
            vehicleId={selectedVehicleId}
            onClose={handleCloseViewModal}
          />
        )}

        {isEditModalOpen && vehicleToEdit && (
          <EditVehicleModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false)
              setVehicleToEdit(null)
              fetchData()
            }}
            vehicleId={vehicleToEdit.id}
            initialVehicleData={vehicleToEdit}
            onSave={handleSaveEditVehicleModal}
          />
        )}
      </>
    )
  }

  return <TableContent />
}

export default VehicleTable

